import React from 'react';
import { useHistory } from 'react-router';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import PropTypes from 'prop-types';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import { lighten } from '@material-ui/core/styles/colorManipulator';
import { ListItemSecondaryAction } from '@material-ui/core';
import useUserContext from 'Context/UserContext';
import useHomeContext from 'Context/HomeContext';
import { Tooltip } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4';

const styles = theme => ({
  root: {
    borderLeft: '3px solid',
    borderLeftColor: 'transparent',
    borderBottom: '1px solid',
    borderBottomColor: lighten(theme.palette.grey[300], 0.10),
    '&:hover': {
      color: theme.palette.facet.primaryBlue,
    },
    '&:hover $icon': {
      color: theme.palette.facet.primaryBlue,
    },
  },
  rootSelected: {
    borderLeft: '3px solid',
    borderLeftColor: theme.palette.facet.primaryBlue,
    color: theme.palette.facet.primaryBlue,
  },
  mobileLinkItem: {
    textAlign: 'center',
    width: '100vw',
    color: theme.palette.grey[500],
    borderBottom: '1px solid',
    borderBottomColor: theme.palette.grey[100],
    '&.Mui-selected': {
      borderLeft: 0,
      color: theme.palette.facet.primaryBlue,
      backgroundColor: lighten(theme.palette.facet.accentBlue, 0.90),
    },
  },
  subLinkItem: {
    borderBottom: 0,
    '&.Mui-selected': {
      borderLeft: 0,
      color: theme.palette.facet.primaryBlue,
      backgroundColor: 'transparent',
    },
    '&.Mui-disabled': {
      opacity: 1,
    },
  },
  icon: {
    width: theme.spacing(2.2),
    height: theme.spacing(2.5),
    color: theme.palette.facet.primaryGray3,
  },
  listItemIconRoot: {
    minWidth: theme.spacing(2.5),
    width: theme.spacing(4.5),
  },
  selectedIcon: {
    color: theme.palette.facet.primaryBlue,
  },
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
    },
    '100%': {
      opacity: 1,
    },
  },
  animatedItem: {
    animation: `$fadeIn 1000ms ${theme.transitions.easing.easeInOut}`,
  },
  secondaryText: {
    color: theme.palette.facet.primaryBlue,
  },
  secondaryTextMobile: {
    color: theme.palette.facet.primaryBlue,
  },
});

const LinkItem = ({
  className,
  classes,
  id,
  icon,
  BadgeComponent,
  BadgeComponentProps,
  label,
  secondaryText,
  to,
  hash,
  isMobileItem,
  isSubLink,
  onClick,
  href,
  secondaryAction,
  drawerOpen,
  secondaryActionProps,
  isSelected,
  test,
  openInNewTab,
}) => {
  const history = useHistory();
  const { householdResource } = useSimpleHousehold();
  const { userResource, agentResource } = useUserContext();
  const { homeResource } = useHomeContext();
  const Icon = icon;
  const SecondaryAction = secondaryAction;
  const isSelectedLink = isSelected && isSelected({ location: history.location });

  const renderTo = () => {
    let path;
    if (typeof to === 'function') {
      path = to({
        householdResource, userResource, agentResource, homeResource,
      });
    } else {
      path = to;
    }

    if (hash) {
      path = `${path}#${hash}`;
    }

    return path;
  };

  return (
    <>
      <ListItem
        id={id}
        disabled={!href && !to}
        selected={isSelectedLink}
        button
        color="inherit"
        className={classnames(classes.root, className, {
          [classes.rootSelected]: isSelectedLink,
          [classes.mobileLinkItem]: isMobileItem,
          [classes.subLinkItem]: isSubLink,
        })}
        component={href ? 'a' : Link}
        to={renderTo()}
        href={href}
        target={href && openInNewTab && '_blank'}
        rel={href && openInNewTab && 'noopener noreferrer'}
        onClick={onClick}
        test={test}
        divider
      >
        {!isMobileItem && Icon && (
        <Tooltip title={label}>
          <ListItemIcon classes={{ root: classes.listItemIconRoot }}>
            <BadgeComponent {...BadgeComponentProps}>
              <Icon
                className={classnames(classes.icon, {
                  [classes.selectedIcon]: isSelectedLink,
                })}
              />
            </BadgeComponent>
          </ListItemIcon>
        </Tooltip>
        )}
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            variant: isMobileItem && !isSubLink ? 'h2' : 'h6',
            noWrap: true,
          }}
          secondary={secondaryText}
          secondaryTypographyProps={{
            className: classnames({
              [classes.secondaryText]: !isMobileItem,
              [classes.secondaryTextMobile]: isMobileItem,
            }),
            variant: isMobileItem && !isSubLink ? 'h2' : 'h6',
            noWrap: true,
          }}
        />
        {SecondaryAction && drawerOpen && (
          <ListItemSecondaryAction
            className={classnames({
              [classes.animatedItem]: drawerOpen,
            })}
          >
            <SecondaryAction {...secondaryActionProps} />
          </ListItemSecondaryAction>)
        }
        {SecondaryAction && isSubLink && (
          <ListItemSecondaryAction
            className={classnames({
              [classes.animatedItem]: drawerOpen,
            })}
          >
            <SecondaryAction {...secondaryActionProps} />
          </ListItemSecondaryAction>)
        }
      </ListItem>
    </>
  );
};

LinkItem.defaultProps = {
  className: '',
  id: undefined,
  isMobileItem: false,
  isSubLink: false,
  onClick: null,
  hash: null,
  to: null,
  href: null,
  icon: null,
  BadgeComponent: React.Fragment,
  secondaryAction: null,
  secondaryActionProps: null,
  isSelected: null,
  test: '',
  secondaryText: null,
  openInNewTab: true,
  BadgeComponentProps: {},
};

LinkItem.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  id: PropTypes.string,
  icon: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.func,
  ]),
  BadgeComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.symbol]),
  label: PropTypes.string.isRequired,
  to: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
  ]),
  hash: PropTypes.string,
  href: PropTypes.string,
  isMobileItem: PropTypes.bool,
  isSubLink: PropTypes.bool,
  onClick: PropTypes.func,
  secondaryAction: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.func,
  ]),
  secondaryActionProps: PropTypes.object,
  isSelected: PropTypes.func,
  test: PropTypes.string,
  secondaryText: PropTypes.string,
  openInNewTab: PropTypes.bool,
  BadgeComponentProps: PropTypes.object,
};

export default withStyles(styles)(LinkItem);
